import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, InputAdornment,
  Tabs, Tab, Card, CardContent, Stack, useTheme, useMediaQuery, TablePagination,
  Grid, MenuItem,
} from '@mui/material';
import { Add, Search, AccountBalanceWallet, Groups, Handshake, Receipt } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { paymentService, projectService, farmOperationService } from '@smartgrow/shared';

const PAGE_SIZE = 10;

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'COMPLETED': return 'success';
    case 'PENDING': return 'warning';
    case 'FAILED': return 'error';
    default: return 'default';
  }
};

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;

export const PaymentsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProjectId = searchParams.get('projectId') ?? '';
  const projectName = searchParams.get('projectName') ?? '';
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [paymentType, setPaymentType] = useState<'ALL' | 'SALARY' | 'CONTRACTOR' | 'EXPENSE'>('ALL');
  const [page, setPage] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState(urlProjectId);
  const [selectedOperationId, setSelectedOperationId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');

  const projectId = selectedProjectId || urlProjectId;

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: operationsData } = useQuery({
    queryKey: ['operations-for-filter', projectId],
    queryFn: () => farmOperationService.getByProject(projectId),
    enabled: !!projectId,
  });

  const { data: tasksData } = useQuery({
    queryKey: ['tasks-for-filter', selectedOperationId],
    queryFn: () => farmOperationService.getTasksByOperation(selectedOperationId),
    enabled: !!selectedOperationId,
  });

  const { data } = useQuery({
    queryKey: ['payments', paymentType, page, projectId, selectedOperationId, selectedTaskId, debouncedSearch],
    queryFn: () => paymentService.search({
      page, size: PAGE_SIZE,
      search: debouncedSearch || undefined,
      type: paymentType === 'ALL' ? undefined : paymentType,
      projectId: projectId || undefined,
      farmOperationId: selectedOperationId || undefined,
      operationTaskId: selectedTaskId || undefined,
    }),
  });

  const clearProjectFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('projectId');
    next.delete('projectName');
    setSearchParams(next);
    setSelectedProjectId('');
    setSelectedOperationId('');
    setSelectedTaskId('');
    setPage(0);
  };

  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value);
    setSelectedOperationId('');
    setSelectedTaskId('');
    setPage(0);
    if (!value && urlProjectId) clearProjectFilter();
  };

  const handleOperationChange = (value: string) => {
    setSelectedOperationId(value);
    setSelectedTaskId('');
    setPage(0);
  };

  const { data: stats } = useQuery({
    queryKey: ['payments-stats', paymentType, projectId, selectedOperationId, selectedTaskId, debouncedSearch],
    queryFn: () => paymentService.getStats({
      search: debouncedSearch || undefined,
      type: paymentType === 'ALL' ? undefined : paymentType,
      projectId: projectId || undefined,
      farmOperationId: selectedOperationId || undefined,
      operationTaskId: selectedTaskId || undefined,
    }),
  });

  const payments = data?.content ?? [];
  const hasReference = payments.some((p) => p.referenceNumber);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">Payments</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/payments/new')} size={isMobile ? 'small' : 'medium'}>
          New Payment
        </Button>
      </Box>

      {urlProjectId && !selectedProjectId && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Project: ${projectName || urlProjectId}`}
            onDelete={clearProjectFilter}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <AccountBalanceWallet color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                  <Typography variant="h6" fontWeight="bold">{fmt(stats?.totalPaid ?? 0)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Groups color="success" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Salary</Typography>
                  <Typography variant="h6" fontWeight="bold">{fmt(stats?.totalSalary ?? 0)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Handshake color="warning" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Contractor</Typography>
                  <Typography variant="h6" fontWeight="bold">{fmt(stats?.totalContractor ?? 0)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Receipt color="info" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Expense</Typography>
                  <Typography variant="h6" fontWeight="bold">{fmt(stats?.totalExpense ?? 0)}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Completed Payments</Typography>
              <Typography variant="h6" fontWeight="bold">{stats?.completedCount ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={paymentType} onChange={(_, v) => { setPaymentType(v); setPage(0); }} variant="scrollable" scrollButtons="auto">
          <Tab label="All" value="ALL" />
          <Tab label="Salary" value="SALARY" />
          <Tab label="Contractor" value="CONTRACTOR" />
          <Tab label="Expense" value="EXPENSE" />
        </Tabs>
      </Paper>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField select size="small" label="Project" value={projectId}
            onChange={(e) => handleProjectChange(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Projects</MenuItem>
            {(projectsData?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Operation" value={selectedOperationId}
            onChange={(e) => handleOperationChange(e.target.value)} disabled={!projectId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Operations</MenuItem>
            {(operationsData?.content ?? []).map((op) => (
              <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Task" value={selectedTaskId}
            onChange={(e) => { setSelectedTaskId(e.target.value); setPage(0); }} disabled={!selectedOperationId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Tasks</MenuItem>
            {(tasksData ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField fullWidth placeholder="Search by reference, recipient..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)} size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Paper>

      {isMobile ? (
        <Stack spacing={2}>
          {payments.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No payments found</Typography>
          )}
          {payments.map((p) => (
            <Card key={p.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/payments/${p.id}`)}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" fontFamily="monospace" color="text.secondary">{p.referenceNumber}</Typography>
                  <Chip label={p.status} color={getStatusColor(p.status)} size="small" />
                </Box>
                <Typography variant="body2" fontWeight="bold">{fmt(p.amount)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {p.recipientUserName || p.contractorName || '—'}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Chip label={p.paymentType} variant="outlined" size="small" />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(p.paymentDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                {hasReference && <TableCell>Reference</TableCell>}
                <TableCell>Type</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={hasReference ? 8 : 7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No payments found</Typography>
                  </TableCell>
                </TableRow>
              )}
              {payments.map((p, i) => (
                <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/payments/${p.id}`)}>
                  <TableCell>{page * PAGE_SIZE + i + 1}</TableCell>
                  {hasReference && (
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{p.referenceNumber}</Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip label={p.paymentType} variant="outlined" size="small" />
                  </TableCell>
                  <TableCell>{p.recipientUserName || p.contractorName || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{fmt(p.amount)}</Typography>
                  </TableCell>
                  <TableCell>{p.paymentMethod.replace('_', ' ')}</TableCell>
                  <TableCell>
                    <Chip label={p.status} color={getStatusColor(p.status)} size="small" />
                  </TableCell>
                  <TableCell>{new Date(p.paymentDate).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data?.totalElements ?? 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        </TableContainer>
      )}
    </Box>
  );
};
